import React from "react";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Check, Menu, X } from "lucide-react";

export default function AboutPage() {
  const [menuOpen, setMenuOpen] = React.useState(false);

  return (
    <div style={{ fontFamily: "Manrope,sans-serif" }} className="bg-[#FAFAFA] text-zinc-900 min-h-screen">
      <Helmet>
        <title>About — PricePAL Compare</title>
        <meta name="description" content="Why we built PricePAL Compare and how we help UK shoppers save money every day." />
      </Helmet>

      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 bg-white/90 backdrop-blur-xl border-b border-zinc-100" data-testid="about-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-14">
          <a href="/" className="flex items-center gap-2" data-testid="about-logo-link">
            <div className="w-7 h-7 rounded-lg bg-emerald-600 flex items-center justify-center"><span className="text-white font-black text-xs" style={{ fontFamily: "Outfit,sans-serif" }}>P</span></div>
            <span className="text-base font-bold text-zinc-900" style={{ fontFamily: "Outfit,sans-serif" }}><span className="hidden sm:inline">PricePAL Compare</span><span className="sm:hidden">PricePAL</span></span>
          </a>
          <div className="flex items-center gap-3">
            <a href="/register" className="bg-emerald-600 text-white hover:bg-emerald-700 font-bold px-4 py-2 rounded-full text-sm transition flex items-center gap-1.5 shadow-sm" data-testid="about-header-cta">
              Get app <ArrowRight className="w-3.5 h-3.5" />
            </a>
            <button className="md:hidden p-1.5 text-zinc-600" onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu" data-testid="about-menu-toggle">
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
          <nav className="hidden md:flex items-center gap-7 ml-8" data-testid="about-nav-desktop">
            <a href="/#how-it-works" className="text-sm font-medium text-zinc-500 hover:text-emerald-600 transition" data-testid="about-nav-howitworks">How it works</a>
            <a href="/#fuel" className="text-sm font-medium text-zinc-500 hover:text-emerald-600 transition" data-testid="about-nav-fuel">Fuel</a>
            <a href="/community" className="text-sm font-medium text-zinc-500 hover:text-emerald-600 transition" data-testid="about-nav-community">Community</a>
            <a href="/about" className="text-sm font-medium text-emerald-600 font-bold" data-testid="about-nav-about">About</a>
            <a href="/contact" className="text-sm font-medium text-zinc-500 hover:text-emerald-600 transition" data-testid="about-nav-contact">Contact</a>
          </nav>
        </div>
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-zinc-100 px-4 py-4 space-y-3" data-testid="about-nav-mobile">
            <a href="/#how-it-works" className="block text-sm font-medium text-zinc-600 py-2" data-testid="about-nav-mobile-howitworks">How it works</a>
            <a href="/#fuel" className="block text-sm font-medium text-zinc-600 py-2" data-testid="about-nav-mobile-fuel">Fuel</a>
            <a href="/community" className="block text-sm font-medium text-zinc-600 py-2" data-testid="about-nav-mobile-community">Community</a>
            <a href="/about" className="block text-sm font-bold text-emerald-600 py-2" data-testid="about-nav-mobile-about">About</a>
            <a href="/contact" className="block text-sm font-medium text-zinc-600 py-2" data-testid="about-nav-mobile-contact">Contact</a>
          </div>
        )}
      </header>

      <div className="h-14" />

      {/* Content */}
      <main className="max-w-2xl mx-auto px-4 sm:px-6 py-10 md:py-16" data-testid="about-main">

        <h1 className="text-2xl sm:text-3xl lg:text-4xl tracking-tight font-black text-zinc-900 mb-4" style={{ fontFamily: "Outfit,sans-serif" }} data-testid="about-heading">
          Why we built PricePAL Compare
        </h1>
        <p className="text-base text-zinc-500 leading-relaxed mb-8" data-testid="about-intro">
          Everyday prices can vary significantly between supermarkets and fuel stations. We built PricePAL Compare to make it easier to compare those prices in one place — whether you're shopping in-store, planning your weekly shop from home or looking for cheaper fuel nearby.
        </p>

        <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-3" style={{ fontFamily: "Outfit,sans-serif" }} data-testid="about-mission-heading">Our mission</h2>
        <p className="text-sm sm:text-base text-zinc-500 leading-relaxed mb-3" data-testid="about-mission-para-1">
          Help UK households make more informed spending decisions by making everyday prices easier to find and compare — across groceries, household essentials, personal care and fuel.
        </p>
        <p className="text-sm sm:text-base text-zinc-500 leading-relaxed mb-8" data-testid="about-mission-para-2">
          We believe comparing everyday prices should be simple, fast and accessible to everyone.
        </p>

        <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-3" style={{ fontFamily: "Outfit,sans-serif" }} data-testid="about-different-heading">What makes us different</h2>
        <ul className="space-y-2.5 mb-8" data-testid="about-different-list">
          {[
            "Price data from multiple sources, including retailers and our community",
            "Compare groceries, household essentials, personal care and fuel",
            "Scan product barcodes while you shop to compare available prices",
            "Community contributions help keep PricePAL's price data current",
            "Dedicated access for NHS and Blue Light members",
          ].map((item, idx) => (
            <li key={item} className="flex items-start gap-2.5" data-testid={`about-different-item-${idx}`}>
              <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5"><Check className="w-3 h-3 text-emerald-600" /></div>
              <span className="text-sm text-zinc-700">{item}</span>
            </li>
          ))}
        </ul>

        <h2 className="text-xl sm:text-2xl font-bold text-zinc-900 mb-3" style={{ fontFamily: "Outfit,sans-serif" }} data-testid="about-users-heading">Built with users</h2>
        <p className="text-sm sm:text-base text-zinc-500 leading-relaxed mb-8" data-testid="about-users-para">
          PricePAL Compare has been developed with feedback from our Alpha and Beta testers, helping us improve the features, usability and overall shopping experience as the platform has grown.
        </p>

        <a href="/register" className="block w-full sm:w-auto sm:inline-flex items-center justify-center gap-2 bg-emerald-600 text-white hover:bg-emerald-700 font-bold px-6 py-3.5 rounded-full text-base text-center transition shadow-sm" data-testid="about-cta">
          Start Your 7-Day Free Trial <ArrowRight className="w-4 h-4 hidden sm:inline" />
        </a>
        <p className="mt-3 text-xs text-zinc-500 leading-snug" data-testid="about-cta-support">
          Card required · No charge today · Premium £1.99/month after your trial · Cancel anytime
        </p>
        <p className="mt-1 text-xs text-zinc-600 leading-snug" data-testid="about-family-line">
          Family £2.99/month after the 7-day free trial
        </p>
      </main>

      {/* Footer */}
      <footer className="bg-zinc-50 py-8 pb-20 sm:pb-8 border-t border-zinc-100 mt-auto" data-testid="about-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <span className="text-xs text-zinc-400" data-testid="about-footer-copyright">&copy; 2026 PricePAL Compare Ltd.</span>
          <nav className="flex gap-5" data-testid="about-footer-nav">
            {[{ label: "About", href: "/about" }, { label: "Contact", href: "/contact" }, { label: "Privacy", href: "/privacy" }, { label: "Terms", href: "/terms" }].map(l => (
              <a key={l.href} href={l.href} className="text-sm text-zinc-400 hover:text-emerald-600 transition py-2 min-h-[44px] flex items-center" data-testid={`about-footer-link-${l.label.toLowerCase()}`}>{l.label}</a>
            ))}
          </nav>
        </div>
      </footer>
    </div>
  );
}
